import { useState } from "react";

import { Button, Checkbox, Switch } from "@chef/components";
import { Setting } from "@chef/icons/large";
import {
  acceptAllConsents,
  rejectAllConsents,
  selectTrackingConsents,
  setConsent,
} from "@chef/state-management/features/tracking/consents";
import {
  closeModal,
  confirmConfiguration,
  ICategories,
  useGetTrackingDestinationsQuery,
} from "@chef/state-management";
import { isNotEmptyArray } from "@chef/utils/array";
import { useAppDispatch, useAppSelector } from "@chef/state-management/hooks";
import { CATEGORIES } from "@chef/smart-components";

import { intl } from "./ConsentModal.intl";
import { Modal } from "../Modal";

const WRITE_KEY = process.env["NX_SEGMENT_WRITE_KEY"] as string;

interface ViewProps {
  consents: ICategories;
  onAcceptAll: () => void;
  onRejectAll?: () => void;
  onClose: () => void;
  onToggleCategory: (category: string) => void;
  onToggleView?: () => void;
}

interface CategoryProps {
  consents: ICategories;
  category: typeof CATEGORIES[number];
  onToggle: () => void;
}

const isEnabled = (consents: ICategories, category: string) => {
  if (category in consents) {
    return consents[category];
  }

  return consents.All;
};

const Category = ({ consents, category, onToggle }: CategoryProps) => {
  const { data: destinations } = useGetTrackingDestinationsQuery({
    writeKey: WRITE_KEY,
  });

  const categoryDestinations =
    destinations
      ?.filter((d) => category.types.includes(d.category))
      // Filter out custom Godtlevert destinations - these are custom
      // segment destinations that are essentially just aliases for
      // other destinations, and should not be informed of
      .filter(
        (d) => !(d.category === "Other" && d.name.includes("(Godtlevert)")),
      ) ?? [];

  return (
    <li key={category.key} className="mb-4 group">
      <div>
        <div className="flex items-end justify-between mb-2">
          <strong>{category.name}</strong>
          <Switch
            label={category.name}
            disabled={category.forced}
            checked={category.forced || isEnabled(consents, category.key)}
            onChange={onToggle}
            small
          />
        </div>

        <div className="mb-4">{category.description}</div>

        {isNotEmptyArray(categoryDestinations) && (
          <ul className="mb-4 text-sm list-disc list-inside">
            {categoryDestinations.map((d) => (
              <li key={d.name}>{d.name}</li>
            ))}
          </ul>
        )}
      </div>

      <hr className="my-4 border-t-1.5 border-grey-3 group-last:hidden" />
    </li>
  );
};

const DetailedView = ({
  consents,
  onToggleCategory,
  onAcceptAll,
  onRejectAll,
  onClose,
}: ViewProps) => {
  return (
    <div className="flex flex-col">
      <div className="h-full">
        <h1 className="mb-4 text-xl">
          <strong>{intl.CONSENT_SETTINGS_TITLE}</strong>
        </h1>

        <p className="mb-4 text-sm">
          {intl.CONSENT_SETTINGS_DESCRIPTION} {intl.READ_MORE}{" "}
          <a
            href={intl.CONSENT_SETTINGS_COOKIE_POLICY_LINK_URL}
            rel="noopener noreferrer"
            target="_blank"
            className="underline"
          >
            <strong>{intl.OUR_COOKIE_POLICY}</strong>
          </a>{" "}
          {intl.AND}{" "}
          <a
            href="https://business.safety.google/privacy/"
            rel="noopener noreferrer"
            target="_blank"
            className="underline"
          >
            <strong>{intl.GOOGLE_PRIVACY_POLICY} </strong>
          </a>
        </p>

        <hr className="mb-4 border-t-2" />

        <div className="mb-4">
          <strong>{intl.CONSENT_SETTINGS_COOKIE_DECLARATION_TITLE}</strong>
        </div>

        <ul className="mb-4 text-sm">
          {CATEGORIES.map((category) => (
            <Category
              key={category.key}
              category={category}
              consents={consents}
              onToggle={() => onToggleCategory(category.key)}
            />
          ))}
        </ul>
      </div>
      <div className="flex flex-col gap-2 py-3 md:gap-4">
        <div className="flex items-stretch gap-2 md:gap-4">
          <Button outlined onClick={onRejectAll} className="grow">
            {intl.CONSENT_SETTINGS_REJECT_ALL}
          </Button>
          <div className="shrink md:grow" />
          <Button outlined onClick={onClose} className="grow">
            {intl.CONSENT_SETTINGS_SAVE_AND_CLOSE}
          </Button>
        </div>
        <Button primary full onClick={onAcceptAll}>
          {intl.CONSENT_SETTINGS_ACCEPT_ALL_COOKIES}
        </Button>
      </div>
    </div>
  );
};

const SimpleView = ({
  consents,
  onToggleCategory,
  onAcceptAll,
  onToggleView,
  onClose,
}: ViewProps) => {
  return (
    <div className="flex flex-col">
      <h1 className="mb-4 text-xl">
        <strong>{intl.CONSENT_SETTINGS_TITLE}</strong>
      </h1>
      <p className="mb-4 text-sm">
        {intl.CONSENT_SETTINGS_DESCRIPTION}{" "}
        <a
          href={intl.CONSENT_SETTINGS_COOKIE_POLICY_LINK_URL}
          rel="noopener noreferrer"
          target="_blank"
          className="underline"
        >
          <strong>
            {intl.READ_MORE} {intl.OUR_COOKIE_POLICY}
          </strong>
        </a>
      </p>
      <ul className="mb-4">
        {CATEGORIES.map((category) => (
          <li key={category.key} className="flex mb-4">
            <Checkbox
              disabled={category.forced}
              checked={category.forced || isEnabled(consents, category.key)}
              onChange={() => onToggleCategory(category.key)}
            />
            <strong>{category.name}</strong>
          </li>
        ))}
      </ul>
      <div
        className="flex flex-col items-center gap-2 md:gap-4 md:flex-nowrap"
        id="cookies"
      >
        <Button primary onClick={onAcceptAll} full id="accept-all-cookies-btn">
          {intl.CONSENT_SETTINGS_ACCEPT_ALL_COOKIES}
        </Button>

        <Button
          outlined
          onClick={onClose}
          full
          className="mb-8"
          id="accept-selected-cookies-btn"
        >
          {intl.CONSENT_SETTINGS_ACCEPT_SELECTED_COOKIES}
        </Button>

        <button onClick={onToggleView}>
          <Setting />{" "}
          <span className="underline">
            {intl.CONSENT_SETTINGS_SHOW_DETAILS}
          </span>
        </button>
      </div>
    </div>
  );
};

export const ConsentModal = () => {
  const dispatch = useAppDispatch();

  const consents = useAppSelector(selectTrackingConsents);
  const [view, setView] = useState<"simple" | "detailed">("simple");

  const handleToggleCategory = (category: string) => {
    dispatch(
      setConsent({ name: category, enabled: !isEnabled(consents, category) }),
    );
  };

  const handleAcceptAll = () => {
    dispatch(acceptAllConsents());
    dispatch(closeModal());
  };

  const handleRejectAll = () => {
    dispatch(rejectAllConsents());
    dispatch(closeModal());
  };

  const handleClose = () => {
    setTimeout(() => setView("simple"), 500);
    dispatch(confirmConfiguration());
    dispatch(closeModal());
  };

  const handleToggleView = () => {
    setView(view === "simple" ? "detailed" : "simple");
  };

  return (
    <Modal panel="card" size={view === "simple" ? "sm" : "md"}>
      {view === "simple" ? (
        <SimpleView
          consents={consents}
          onAcceptAll={handleAcceptAll}
          onToggleCategory={handleToggleCategory}
          onClose={handleClose}
          onToggleView={handleToggleView}
        />
      ) : (
        <DetailedView
          consents={consents}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onClose={handleClose}
          onToggleCategory={handleToggleCategory}
        />
      )}
    </Modal>
  );
};
